import {isAxiosError} from "axios"
import type {
    DocumentType,
    OtkDefectSeverity,
    OtkDocumentDetail,
    OtkDocumentDraftPatchOperation,
    OtkDocumentPhoto,
    OtkDocumentResult,
    OtkEmissionTestType,
    OtkSubjectPermit,
    PhotoCategoryId,
    SignedOtkDocumentPayload
} from "shared"
import {dangerousGoodsVehiclePurpose, deriveDangerousGoodsSubmitField, otkDocumentDetailSchema,} from "shared"

// document-editor.md's DE1 tree — the full 18-code category list, default "M1".
export const vehicleCategories = [
  "M1",
  "M2",
  "M3",
  "N1",
  "N2",
  "N3",
  "L1e",
  "L2e",
  "L3e",
  "L4e",
  "L5e",
  "L6e",
  "L7e",
  "O1",
  "O2",
  "O3",
  "O4",
  "T",
] as const
export type VehicleCategory = (typeof vehicleCategories)[number]

// DE1 tree — "Вантажність, т." only renders for these categories.
export const cargoCapacityCategories = new Set<VehicleCategory>([
  "N1",
  "N2",
  "N3",
  "O1",
  "O2",
  "O3",
  "O4",
])

export function isM2M3(category: string): boolean {
  return category === "M2" || category === "M3"
}

export type VehicleBrandRef = {
  brandId: number
  brandName: string
}

// Advanced-search "Марка"/"Модель" dropdowns — same reference data as
// otk/wireframes's `brands`/`models` mocks, so both apps show identical
// values until a real vehicle-registry API exists. `VehicleTab`'s own
// Марка/Модель stay free text (unlike this closed 19-brand list) since a
// production vehicle can be any real-world make/model.
export const vehicleBrands: VehicleBrandRef[] = [
  { brandId: 1, brandName: "TOYOTA" },
  { brandId: 2, brandName: "VOLKSWAGEN" },
  { brandId: 3, brandName: "ŠKODA" },
  { brandId: 4, brandName: "RENAULT" },
  { brandId: 5, brandName: "MERCEDES-BENZ" },
  { brandId: 6, brandName: "BMW" },
  { brandId: 7, brandName: "HYUNDAI" },
  { brandId: 8, brandName: "KIA" },
  { brandId: 9, brandName: "NISSAN" },
  { brandId: 10, brandName: "FORD" },
  { brandId: 11, brandName: "OPEL" },
  { brandId: 12, brandName: "ЗАЗ" },
  { brandId: 13, brandName: "ВАЗ" },
  { brandId: 14, brandName: "MAN" },
  { brandId: 15, brandName: "Богдан" },
  { brandId: 16, brandName: "IVECO" },
  { brandId: 17, brandName: "DAF" },
  { brandId: 18, brandName: "SCANIA" },
  { brandId: 19, brandName: "VOLVO" },
]

export type VehicleModelRef = {
  modelId: number
  modelName: string
  brandId: number
}

export const vehicleModels: VehicleModelRef[] = [
  { modelId: 1, modelName: "COROLLA", brandId: 1 },
  { modelId: 2, modelName: "CAMRY", brandId: 1 },
  { modelId: 3, modelName: "RAV4", brandId: 1 },
  { modelId: 4, modelName: "GOLF", brandId: 2 },
  { modelId: 5, modelName: "PASSAT", brandId: 2 },
  { modelId: 6, modelName: "TIGUAN", brandId: 2 },
  { modelId: 7, modelName: "OCTAVIA", brandId: 3 },
  { modelId: 8, modelName: "FABIA", brandId: 3 },
  { modelId: 9, modelName: "KODIAQ", brandId: 3 },
  { modelId: 10, modelName: "DUSTER", brandId: 4 },
  { modelId: 11, modelName: "LOGAN", brandId: 4 },
  { modelId: 12, modelName: "MEGANE", brandId: 4 },
  { modelId: 13, modelName: "SPRINTER", brandId: 5 },
  { modelId: 14, modelName: "VITO", brandId: 5 },
  { modelId: 15, modelName: "E-CLASS", brandId: 5 },
  { modelId: 16, modelName: "X5", brandId: 6 },
  { modelId: 17, modelName: "3 SERIES", brandId: 6 },
  { modelId: 18, modelName: "TUCSON", brandId: 7 },
  { modelId: 19, modelName: "ELANTRA", brandId: 7 },
  { modelId: 20, modelName: "SPORTAGE", brandId: 8 },
  { modelId: 21, modelName: "RIO", brandId: 8 },
  { modelId: 22, modelName: "QASHQAI", brandId: 9 },
  { modelId: 23, modelName: "ALMERA", brandId: 9 },
  { modelId: 24, modelName: "TRANSIT", brandId: 10 },
  { modelId: 25, modelName: "FOCUS", brandId: 10 },
  { modelId: 26, modelName: "ASTRA", brandId: 11 },
  { modelId: 27, modelName: "VIVARO", brandId: 11 },
  { modelId: 28, modelName: "SLAVUTA", brandId: 12 },
  { modelId: 29, modelName: "SENS", brandId: 12 },
  { modelId: 30, modelName: "2107", brandId: 13 },
  { modelId: 31, modelName: "2110", brandId: 13 },
  { modelId: 32, modelName: "TGL 12.250", brandId: 14 },
  { modelId: 33, modelName: "TGX", brandId: 14 },
  { modelId: 34, modelName: "А092", brandId: 15 },
  { modelId: 35, modelName: "А069", brandId: 15 },
  { modelId: 36, modelName: "DAILY", brandId: 16 },
  { modelId: 37, modelName: "STRALIS", brandId: 16 },
  { modelId: 38, modelName: "XF", brandId: 17 },
  { modelId: 39, modelName: "CF", brandId: 17 },
  { modelId: 40, modelName: "R-SERIES", brandId: 18 },
  { modelId: 41, modelName: "FH", brandId: 19 },
  { modelId: 42, modelName: "CRAFTER", brandId: 2 },
]

export type VehicleKindRef = {
  kindId: number
  kind: string
}

// Advanced-search "Тип ТЗ" dropdown — same closed list as
// otk/wireframes's `vehicleTypes` mock; distinct from `vehicleCategories`
// (the M1/M2/N1... DE1 codes), which is its own already-implemented field.
export const vehicleKinds: VehicleKindRef[] = [
  { kindId: 1, kind: "ЛЕГКОВИЙ" },
  { kindId: 2, kind: "ВАНТАЖНИЙ" },
  { kindId: 3, kind: "АВТОБУС" },
  { kindId: 4, kind: "ПРИЧІП" },
  { kindId: 5, kind: "НАПІВПРИЧІП" },
  { kindId: 6, kind: "МОТОЦИКЛ" },
  { kindId: 7, kind: "МОПЕД" },
  { kindId: 8, kind: "ТРАКТОР КОЛІСНИЙ" },
  { kindId: 9, kind: "СПЕЦІАЛЬНИЙ" },
]

// DE1 tree — "Екологічний рівень" Select options, verbatim, in order.
export const ecologyLevels = [
  "Євро-0",
  "Євро-1",
  "Євро-2",
  "Євро-3",
  "Євро-4",
  "Євро-5",
  "Євро-5 EEV",
  "Євро-6",
] as const

// DE1 tree — "Періодичність проходження ОТК" RadioGroup options, verbatim.
export const otkPeriodicityOptions = ["6 місяців", "1 рік", "2 роки"] as const

// "Призначення ТЗ" checkbox list, verbatim, in order (`vehiclePurposes`,
// static/business-defined, 13 entries) — document-editor.md, right after DE1.
export const vehiclePurposes = [
  "ТЗ з видачею ЄКМТ",
  "ТЗ кат. M2 або M3 для використання на міжнародних автобусних маршрутах",
  "ТЗ кат. M2 або M3 для використання на міжміських автобусних маршрутах",
  "ТЗ кат. M2 або M3 для використання на приміських автобусних маршрутах",
  "ТЗ кат. M2 або M3 для використання на міських автобусних маршрутах",
  "ТЗ кат. M2 або M3 для перевезення пасажирів",
  "Автобус, призначений для перевезення школярів",
  "Автобус, призначений для перевезення осіб з інвалідністю",
  "Таксі",
  "Спеціалізований санітарний автомобіль бригади екстреної медичної допомоги",
  "Великогабаритний, великоваговий транспортний засіб",
  dangerousGoodsVehiclePurpose,
  "Навчальний транспортний засіб",
] as const
export const TAXI_PURPOSE = "Таксі"
export const TRAINING_PURPOSE = "Навчальний транспортний засіб"

// DE5 tree — "Тип документа, що посвідчує особу" Select options, verbatim,
// shared by the owner's and the recipient's identity-document blocks.
export const identityDocumentTypes = [
  "Посвідчення водія українське",
  "Паспорт громадянина України для виїзду за кордон",
  "Тимчасове посвідчення громадянина України",
  "Паспорт громадянина України",
] as const

// DE6 tree — "Тип випробування спалин" Select, default "NO_EMISSIONS".
export const emissionTestModes = [
  { value: "PETROL_GAS", label: "Бензин та/або газове паливо" },
  { value: "DIESEL", label: "Дизель або газодизель" },
  { value: "NO_EMISSIONS", label: "Спалини відсутні" },
] as const
export type EmissionTestMode = (typeof emissionTestModes)[number]["value"]

export interface PhotoCategoryDef {
  id: PhotoCategoryId
  group: string
  label: string
  multiple?: boolean
}

export const photoGroups = [
  "Основні фотоматеріали",
  "Фото за умовами експлуатації",
  "Додаткові фотоматеріали",
] as const

export const photoCategories: PhotoCategoryDef[] = [
  {
    id: "DASHBOARD",
    group: "Основні фотоматеріали",
    label:
      "Панель приладів ТЗ із показником загального пробігу ТЗ, зафіксованим на одометрі або тахографі",
  },
  {
    id: "RIGHT_SIDE",
    group: "Основні фотоматеріали",
    label:
      "Заг. вигляд правої сторони ТЗ з увімкненими габаритними та контурними вогнями",
  },
  {
    id: "LEFT_SIDE",
    group: "Основні фотоматеріали",
    label:
      "Заг. вигляд лівої сторони ТЗ з увімкненими габаритними та контурними вогнями",
  },
  {
    id: "FRONT",
    group: "Основні фотоматеріали",
    label:
      "Заг. вигляд передньої частини ТЗ з фіксацією НЗ під час перевірки увімкнених фар, аварійної сигналізації (за наявності), габаритних, контурних вогнів",
  },
  {
    id: "REAR",
    group: "Основні фотоматеріали",
    label:
      "Заг. вигляд задньої частини ТЗ з фіксацією НЗ на гальм. стенді під час перевірки методом стендових випробувань з увімкненими сигналами гальмування, аварійної сигналізації (за наявності), габаритними та контурними вогнями",
  },
  {
    id: "INTERIOR_M2_M3",
    group: "Фото за умовами експлуатації",
    label: "Салон ТЗ категорій М2, М3",
  },
  {
    id: "PEDALS",
    group: "Фото за умовами експлуатації",
    label:
      "Додаткові педалі та їх кріплення для спеціаліста з питань навчання керуванню ТЗ",
  },
  {
    id: "MIRROR",
    group: "Фото за умовами експлуатації",
    label: "Дзеркало/а заднього виду для спеціаліста з навчання керуванню ТЗ",
  },
  {
    id: "TACHOGRAPH",
    group: "Фото за умовами експлуатації",
    label: "Тахограф в увімкненому стані",
  },
  {
    id: "TACHOGRAPH_PLATE",
    group: "Фото за умовами експлуатації",
    label: "Табличка перевірки та адаптації тахографа",
  },
  {
    id: "ADDITIONAL",
    group: "Додаткові фотоматеріали",
    label: "Додаткові фотографії (можна кілька)",
    multiple: true,
  },
]

export function isPhotoCategoryVisible(
  id: PhotoCategoryId,
  vehicleCategory: string,
  hasTachograph: boolean | null
): boolean {
  if (id === "INTERIOR_M2_M3") return isM2M3(vehicleCategory)
  if (id === "TACHOGRAPH" || id === "TACHOGRAPH_PLATE")
    return hasTachograph === true
  return true
}

export const MAX_PHOTO_BYTES = 5 * 1024 * 1024

// DE8 — sentence-case plural severity labels, matching
// technical_inspection's own `nonconformitySeverityLabels` exactly.
export const severityLabels: Record<OtkDefectSeverity, string> = {
  MINOR: "Незначні",
  MAJOR: "Значні",
  DANGEROUS: "Небезпечні",
}

export function severityBadgeVariant(
  severity: OtkDefectSeverity
): "destructive" | "default" | "secondary" {
  if (severity === "DANGEROUS") return "destructive"
  if (severity === "MAJOR") return "default"
  return "secondary"
}

export interface NonConformityCode {
  id: string
  code: string
  description: string
  severity: OtkDefectSeverity
}

export interface UnlistedIssue {
  id: string
  type: OtkDefectSeverity
  description: string
}

export interface Conversion {
  id: number
  description: string
  approved: boolean
  docName: string
  docNumber: string
  docDate: string
}

// dokument-otk.md rules 2–4, shared by the header metadata row and the
// wizard's own derived state — see document-editor.md's "Current
// derived-outcome rule". `deriveOutcome` used to also return
// `repeatInspectionReasons` (fed only the header's "Повторний ОТК" row);
// that row was removed 12.09.2026 (customer request — no norm requires
// showing the term on the document) and the field was deleted with it.
export function deriveOutcome({
  selectedCodes,
  unlistedIssues,
  conversions,
}: {
  selectedCodes: NonConformityCode[]
  unlistedIssues: UnlistedIssue[]
  conversions: Conversion[]
}): { actReasons: string[] } {
  const actReasons: string[] = []

  for (const code of selectedCodes) {
    if (code.severity === "MAJOR" || code.severity === "DANGEROUS") {
      actReasons.push(`${code.code}: ${code.description}`)
    }
  }
  for (const issue of unlistedIssues) {
    if (issue.type === "MAJOR" || issue.type === "DANGEROUS") {
      actReasons.push(issue.description)
    }
  }
  for (const conversion of conversions) {
    if (!conversion.approved) {
      actReasons.push(
        `Непогоджене переобладнання: ${conversion.description || "без опису"}`
      )
    }
  }

  return { actReasons }
}

// --- Per-tab form state, lifted in DocumentEdit so switching wizard
// sections never loses field values (document-editor.md's own rule). ---

export type OwnerType = "INDIVIDUAL" | "LEGAL_ENTITY"
export type RecipientMode = "owner" | "poa"
/** `YesNoRadio`'s own three states — `null` maps to an unselected radio. */
export type YesNo = boolean | null

export interface VehicleTabState {
  plate: string
  registrationCertificate: string
  vin: string
  manufactureYear: string
  registrationDate: string
  brand: string
  model: string
  printName: string
  category: string
  vehicleType: string
  ecologyLevel: string
  bodyNumber: string
  chassisNumber: string
  cargoCapacity: string
  currentOdometer: string
  previousOdometer: string
  subjectToInspection: YesNo
  periodicity: string
  nextInspectionDate: string
  purposes: string[]
  isTaxi: YesNo
  internationalInspection: YesNo
  hasTachograph: YesNo
  notes: string
}

export const initialVehicleState: VehicleTabState = {
  plate: "",
  registrationCertificate: "",
  vin: "",
  manufactureYear: "",
  registrationDate: "",
  brand: "",
  model: "",
  printName: "",
  category: "M1",
  vehicleType: "",
  ecologyLevel: "",
  bodyNumber: "",
  chassisNumber: "",
  cargoCapacity: "",
  currentOdometer: "",
  previousOdometer: "",
  subjectToInspection: null,
  periodicity: "",
  nextInspectionDate: "",
  purposes: [],
  isTaxi: null,
  internationalInspection: null,
  hasTachograph: null,
  notes: "",
}

export interface OwnerTabState {
  ownerType: OwnerType
  fullName: string
  birthDate: string
  taxId: string
  orgName: string
  edrpou: string
  phone: string
  email: string
  address: string
  idDocType: string
  idDocSeries: string
  idDocNumber: string
  idDocIssuedBy: string
  idDocIssuedAt: string
  documentNotes: string
  recipientMode: RecipientMode
  receiptDocSeries: string
  receiptDocNumber: string
  receiptDocDate: string
  recFullName: string
  recBirthDate: string
  recIdDocType: string
  recIdDocSeries: string
  recIdDocNumber: string
  recIdDocIssuedBy: string
  recIdDocIssuedAt: string
}

export const initialOwnerState: OwnerTabState = {
  ownerType: "INDIVIDUAL",
  fullName: "",
  birthDate: "",
  taxId: "",
  orgName: "",
  edrpou: "",
  phone: "",
  email: "",
  address: "",
  idDocType: "",
  idDocSeries: "",
  idDocNumber: "",
  idDocIssuedBy: "",
  idDocIssuedAt: "",
  documentNotes: "",
  recipientMode: "owner",
  receiptDocSeries: "",
  receiptDocNumber: "",
  receiptDocDate: "",
  recFullName: "",
  recBirthDate: "",
  recIdDocType: "",
  recIdDocSeries: "",
  recIdDocNumber: "",
  recIdDocIssuedBy: "",
  recIdDocIssuedAt: "",
}

export interface TestsTabState {
  videoUrl: string
  totalBrakingForce: string
  imbalanceRatio: string
  brakeResponseTime: string
  parkingBrakeForce: string
  ovality: string
  emissionMode: EmissionTestMode
  coIdle: string
  cnhmIdle: string
  coElevated: string
  cnhmElevated: string
  lambda: string
  smokeK: string
  windshieldExempt: boolean
  windshield: string
  leftExempt: boolean
  left: string
  rightExempt: boolean
  right: string
  rearExempt: boolean
  rear: string
}

export const initialTestsState: TestsTabState = {
  videoUrl: "",
  totalBrakingForce: "",
  imbalanceRatio: "",
  brakeResponseTime: "",
  parkingBrakeForce: "",
  ovality: "",
  emissionMode: "NO_EMISSIONS",
  coIdle: "",
  cnhmIdle: "",
  coElevated: "",
  cnhmElevated: "",
  lambda: "",
  smokeK: "",
  windshieldExempt: true,
  windshield: "",
  leftExempt: true,
  left: "",
  rightExempt: true,
  right: "",
  rearExempt: true,
  rear: "",
}

export interface PhotoUpload {
  name: string
  size: number
  url: string
  error?: string
  /** Blob storage pathname, once known — from `upload()`'s resolved result
   * (may differ from the requested path via Vercel's `addRandomSuffix`) or
   * from a server-loaded file. Needed to target `DELETE /:id/files/:pathname`. */
  pathname?: string
  uploading?: boolean
}

export interface PhotosTabState {
  photos: Partial<Record<PhotoCategoryId, PhotoUpload>>
  additional: PhotoUpload[]
  notes: Partial<Record<PhotoCategoryId, string>>
}

export const initialPhotosState: PhotosTabState = {
  photos: {},
  additional: [],
  notes: {},
}

// Maps `GET /:id/files/with-url`'s `OtkDocumentPhoto[]` (server-stored
// files, presigned `url` already attached) back into the tab's local shape
// — `ADDITIONAL` entries go to the list, everything else keys by category.
export function photosStateFromFiles(
  files: OtkDocumentPhoto[]
): Pick<PhotosTabState, "photos" | "additional"> {
  const photos: Partial<Record<PhotoCategoryId, PhotoUpload>> = {}
  const additional: PhotoUpload[] = []
  for (const file of files) {
    const upload: PhotoUpload = {
      name: file.fileName,
      size: file.size,
      url: file.url ?? "",
      pathname: file.pathname,
    }
    if (file.property === "ADDITIONAL") {
      additional.push(upload)
    } else {
      photos[file.property] = upload
    }
  }
  return { photos, additional }
}

export interface NonConformitiesTabState {
  found: YesNo
  selectedCodes: NonConformityCode[]
  hasUnlisted: boolean
  unlistedIssues: UnlistedIssue[]
}

export const initialNonConformitiesState: NonConformitiesTabState = {
  found: null,
  selectedCodes: [],
  hasUnlisted: false,
  unlistedIssues: [],
}

export interface ReequipmentTabState {
  hasReequipment: YesNo
  conversions: Conversion[]
}

export const initialReequipmentState: ReequipmentTabState = {
  hasReequipment: null,
  conversions: [],
}

// --- `OtkDocumentDetail` → tab-state mapping, seeding the editor's local
// state when opening an existing draft. Only fields the fetched detail
// actually carries are overwritten; UI-only fields absent from the shared
// `OtkDocumentDetail` shape (printName, vehicleType, cargoCapacity, …) keep their
// `initial*State` default. Photos aren't mapped here — `OtkDocumentDetail`
// only carries EDRTZ photo metadata (`photoId`/`note`), not the uploaded
// file/URL `PhotosTabState` needs.

function numberToInput(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : String(value)
}

const engineTypeToEmissionMode: Record<OtkEmissionTestType, EmissionTestMode> = {
  GASOLINE_GAS: "PETROL_GAS",
  DIESEL_GAS_DIESEL: "DIESEL",
  NO_EMISSIONS: "NO_EMISSIONS",
}

// Inverse of the map above — `TESTS_FIELD_OPS` (DocumentEdit.tsx) uses this
// to translate the tab's `emissionMode` back into `testResults.engineType`
// before sending a PATCH.
export const emissionModeToEngineType: Record<EmissionTestMode, OtkEmissionTestType> = {
  PETROL_GAS: "GASOLINE_GAS",
  DIESEL: "DIESEL_GAS_DIESEL",
  NO_EMISSIONS: "NO_EMISSIONS",
}

export function vehicleStateFromDetail(
  detail: OtkDocumentDetail
): VehicleTabState {
  const vehicle = detail.vehicle
  return {
    ...initialVehicleState,
    plate: vehicle?.plateNumber ?? "",
    registrationCertificate: vehicle?.registrationCertificate ?? "",
    vin: vehicle?.vin ?? "",
    manufactureYear: numberToInput(vehicle?.manufactureYear),
    registrationDate: vehicle?.registrationDate ?? "",
    brand: vehicle?.brand ?? "",
    model: vehicle?.model ?? "",
    category: vehicle?.category ?? initialVehicleState.category,
    ecologyLevel: vehicle?.emissionLevel ?? "",
    vehicleType: vehicle?.type ?? "",
    bodyNumber: vehicle?.bodyNumber ?? "",
    chassisNumber: vehicle?.chassisNumber ?? "",
    currentOdometer: numberToInput(vehicle?.currentOdometer),
    previousOdometer: numberToInput(vehicle?.previousOdometer),
    purposes: vehicle?.purposes ?? [],
    isTaxi: detail.isTaxi ?? null,
    notes: detail.notes ?? "",
  }
}

export function ownerStateFromDetail(detail: OtkDocumentDetail): OwnerTabState {
  const owner = detail.owner
  const receiver = detail.receiver
  return {
    ...initialOwnerState,
    ownerType: owner?.type ?? initialOwnerState.ownerType,
    fullName: owner?.fullName ?? "",
    birthDate: owner?.dateOfBirth ?? "",
    taxId: owner?.taxId ?? "",
    orgName: owner?.organizationName ?? "",
    edrpou: owner?.edrpou ?? "",
    phone: owner?.phone ?? "",
    email: owner?.email ?? "",
    address: owner?.postalAddress ?? "",
    idDocType: owner?.idDocumentType ?? "",
    idDocSeries: owner?.idDocumentSeries ?? "",
    idDocNumber: owner?.idDocumentNumber ?? "",
    idDocIssuedBy: owner?.idDocumentIssuer ?? "",
    idDocIssuedAt: owner?.idDocumentIssueDate ?? "",
    recipientMode: detail.receiverIsOwner === false ? "poa" : "owner",
    recFullName: receiver?.fullName ?? "",
    recBirthDate: receiver?.dateOfBirth ?? "",
    recIdDocType: receiver?.idDocumentType ?? "",
    recIdDocSeries: receiver?.idDocumentSeries ?? "",
    recIdDocNumber: receiver?.idDocumentNumber ?? "",
    recIdDocIssuedBy: receiver?.idDocumentIssuer ?? "",
    recIdDocIssuedAt: receiver?.idDocumentIssueDate ?? "",
    receiptDocSeries: receiver?.powerOfAttorneySeries ?? "",
    receiptDocNumber: receiver?.powerOfAttorneyNumber ?? "",
    receiptDocDate: receiver?.powerOfAttorneyDate ?? "",
  }
}

export function testsStateFromDetail(detail: OtkDocumentDetail): TestsTabState {
  const t = detail.testResults
  if (!t) return initialTestsState
  return {
    ...initialTestsState,
    videoUrl: t.videoUrl ?? "",
    totalBrakingForce: numberToInput(t.totalSpecificBrakingForce),
    imbalanceRatio: numberToInput(t.brakeForceImbalanceRatio),
    brakeResponseTime: numberToInput(t.brakeResponseTime),
    parkingBrakeForce: numberToInput(t.parkingBrakeSpecificForce),
    ovality: numberToInput(t.brakeForceOvality),
    emissionMode: t.engineType
      ? engineTypeToEmissionMode[t.engineType]
      : initialTestsState.emissionMode,
    coIdle: numberToInput(t.coAtIdleRpm),
    cnhmIdle: numberToInput(t.cnhmAtIdleRpm),
    coElevated: numberToInput(t.coAtElevatedRpm),
    cnhmElevated: numberToInput(t.cnhmAtElevatedRpm),
    lambda: numberToInput(t.lambda),
    smokeK: numberToInput(t.smokeOpacityK),
    windshieldExempt: t.windshieldMarked ?? initialTestsState.windshieldExempt,
    windshield: numberToInput(t.windshield),
    leftExempt: t.leftSideMarked ?? initialTestsState.leftExempt,
    left: numberToInput(t.leftSide),
    rightExempt: t.rightSideMarked ?? initialTestsState.rightExempt,
    right: numberToInput(t.rightSide),
    rearExempt: t.rearWindowMarked ?? initialTestsState.rearExempt,
    rear: numberToInput(t.rearWindow),
  }
}

// Standard items' code/description/severity come from the persisted item
// itself (`saveDocumentNonconformities` resolves and stores them
// server-side from the registry at save time) — not from the client-side
// `nonConformityCodes` search dataset, which is only a small mock list until
// the real registry endpoint exists (see `NonConformitiesTab.tsx`).
export function nonConformitiesStateFromDetail(
  detail: OtkDocumentDetail
): NonConformitiesTabState {
  const items = detail.nonconformities
  if (items === undefined) {
    return { ...initialNonConformitiesState, found: detail.hasNonconformities ?? null }
  }
  const selectedCodes: NonConformityCode[] = []
  const unlistedIssues: UnlistedIssue[] = []
  for (const item of items) {
    if (item.form === "STANDARD" && item.nonconformityCodeId) {
      selectedCodes.push({
        id: item.nonconformityCodeId,
        code: item.nonconformityCodeId,
        description: item.description ?? "",
        severity: item.defectType ?? "MINOR",
      })
    } else if (item.form === "NONSTANDARD") {
      unlistedIssues.push({
        id: crypto.randomUUID(),
        type: item.defectType ?? "MINOR",
        description: item.description ?? "",
      })
    }
  }
  return {
    found: detail.hasNonconformities ?? (items.length > 0 ? true : null),
    selectedCodes,
    hasUnlisted: unlistedIssues.length > 0,
    unlistedIssues,
  }
}

export function reequipmentStateFromDetail(
  detail: OtkDocumentDetail
): ReequipmentTabState {
  const reconstructions = detail.reconstructions
  if (reconstructions === undefined) return initialReequipmentState
  if (reconstructions.length === 0) return { hasReequipment: false, conversions: [] }
  return {
    hasReequipment: true,
    conversions: reconstructions.map((reconstruction, index) => ({
      id: index + 1,
      description: reconstruction.description ?? "",
      approved: reconstruction.approved,
      docName: reconstruction.documentName ?? "",
      docNumber: reconstruction.documentNumber ?? "",
      docDate: reconstruction.documentDate ?? "",
    })),
  }
}

// document-editor.md's Conditional-rendering-rules table row for
// `DocumentWizardNav`'s completion/validation icon and the signing trigger.
export function isVehicleTabComplete(v: VehicleTabState): boolean {
  return Boolean(
    v.plate &&
      v.vin &&
      v.brand &&
      v.model &&
      v.manufactureYear &&
      v.currentOdometer &&
      // «Автомобіль експлуатується як таксі» must be explicitly answered
      // before signing — `!== null`, not truthiness: «Ні» (`false`) is a
      // real answer, and `Boolean(false)` would reject it. Stricter than the
      // norm, which demands no particular value; `isTaxi` is required in
      // `OtkDocumentSubmit` regardless.
      v.isTaxi !== null
  )
}

// document-editor.md's vehicle-tab invalid state: the selected category is
// outside the subject's own permit, or the permit itself is suspended/
// revoked — signing must block, though the draft can still be saved.
// `permit` is `undefined`/`null` until the subject profile has loaded, or
// while the vehicle category is still empty — neither is treated as
// invalid, only "not yet known".
export function isVehicleCategoryOutsidePermit(
  category: string,
  permit: OtkSubjectPermit | null | undefined
): boolean {
  if (!permit || !category) return false
  if (permit.status !== "ДІЄ") return true
  return !permit.kategorii.includes(category)
}

export function isOwnerTabComplete(o: OwnerTabState): boolean {
  if (o.ownerType === "INDIVIDUAL") {
    return Boolean(o.fullName && o.taxId && o.idDocType && o.idDocNumber)
  }
  return Boolean(o.orgName && o.edrpou)
}

export function isTestsTabComplete(t: TestsTabState): boolean {
  return Boolean(
    t.totalBrakingForce &&
      t.imbalanceRatio &&
      t.brakeResponseTime &&
      t.parkingBrakeForce
  )
}

export function visiblePhotoCategories(
  vehicleCategory: string,
  hasTachograph: YesNo
): PhotoCategoryDef[] {
  return photoCategories.filter(
    (c) =>
      c.id !== "ADDITIONAL" &&
      isPhotoCategoryVisible(c.id, vehicleCategory, hasTachograph)
  )
}

export function isPhotosTabComplete(
  p: PhotosTabState,
  vehicleCategory: string,
  hasTachograph: YesNo
): boolean {
  return visiblePhotoCategories(vehicleCategory, hasTachograph).every((c) => {
    const photo = p.photos[c.id]
    return Boolean(photo && !photo.error)
  })
}

export function isPhotosTabInvalid(
  p: PhotosTabState,
  vehicleCategory: string,
  hasTachograph: YesNo
): boolean {
  return visiblePhotoCategories(vehicleCategory, hasTachograph).some(
    (c) => p.photos[c.id]?.error
  )
}

export function isNonConformitiesTabComplete(
  n: NonConformitiesTabState
): boolean {
  if (n.found === false) return true
  if (n.found !== true) return false
  return n.selectedCodes.length > 0 || n.unlistedIssues.length > 0
}

export function isReequipmentTabComplete(r: ReequipmentTabState): boolean {
  if (r.hasReequipment === false) return true
  if (r.hasReequipment !== true) return false
  if (r.conversions.length === 0) return false
  return r.conversions.every(
    (c) =>
      c.description.trim() !== "" &&
      (!c.approved || (c.docName && c.docNumber && c.docDate))
  )
}

// --- `DuplicateDialog` — copies a source draft's own already-saved field
// values onto a freshly created draft, using the same endpoints the editor's
// own autosave already calls (`PATCH /otk/documents/:id`, `PUT
// .../nonconformities`), rather than a dedicated duplicate endpoint that
// doesn't exist server-side yet. `store/documents.ts`'s `duplicate` action
// orchestrates the create → copy → refetch sequence; these two functions
// only build the request bodies from the source's `OtkDocumentDetail`.

// Skips `undefined`/`null`/empty-string source values instead of explicitly
// clearing the new draft's field with them — duplicating a filled-in
// section should carry over what's there, not blank out a field the source
// never had (and some of `editableDraftFields`' schemas, e.g.
// `receiver.fullName`, reject an empty string outright, which would fail
// the whole patch since `patchDraftDocument` validates all operations in
// one request).
export function buildDuplicatePatchOperations(
  detail: OtkDocumentDetail,
  sectionIds: string[]
): OtkDocumentDraftPatchOperation[] {
  const ops: OtkDocumentDraftPatchOperation[] = []
  function copy(path: string, value: unknown) {
    if (value === undefined || value === null || value === "") return
    ops.push({ op: "set", path, value })
  }

  if (sectionIds.includes("vehicle") && detail.vehicle) {
    const v = detail.vehicle
    copy("vehicle.plateNumber", v.plateNumber)
    copy("vehicle.registrationCertificate", v.registrationCertificate)
    copy("vehicle.vin", v.vin)
    copy("vehicle.bodyNumber", v.bodyNumber)
    copy("vehicle.chassisNumber", v.chassisNumber)
    copy("vehicle.brand", v.brand)
    copy("vehicle.model", v.model)
    copy("vehicle.category", v.category)
    copy("vehicle.type", v.type)
    copy("vehicle.manufactureYear", v.manufactureYear)
    copy("vehicle.registrationDate", v.registrationDate)
    copy("vehicle.emissionLevel", v.emissionLevel)
    copy("vehicle.currentOdometer", v.currentOdometer)
    copy("vehicle.previousOdometer", v.previousOdometer)
    copy("vehicle.purposes", v.purposes)
    copy("isTaxi", detail.isTaxi)
  }

  if (sectionIds.includes("owner") && detail.owner) {
    const o = detail.owner
    copy("owner.type", o.type)
    copy("owner.fullName", o.fullName)
    copy("owner.taxId", o.taxId)
    copy("owner.organizationName", o.organizationName)
    copy("owner.edrpou", o.edrpou)
    copy("owner.postalAddress", o.postalAddress)
    copy("owner.phone", o.phone)
    copy("owner.email", o.email)
    copy("owner.dateOfBirth", o.dateOfBirth)
    copy("owner.idDocumentType", o.idDocumentType)
    copy("owner.idDocumentSeries", o.idDocumentSeries)
    copy("owner.idDocumentNumber", o.idDocumentNumber)
    copy("owner.idDocumentIssuer", o.idDocumentIssuer)
    copy("owner.idDocumentIssueDate", o.idDocumentIssueDate)
  }

  if (sectionIds.includes("receiver")) {
    copy("receiverIsOwner", detail.receiverIsOwner)
    if (detail.receiver) {
      const r = detail.receiver
      copy("receiver.fullName", r.fullName)
      copy("receiver.dateOfBirth", r.dateOfBirth)
      copy("receiver.powerOfAttorneySeries", r.powerOfAttorneySeries)
      copy("receiver.powerOfAttorneyNumber", r.powerOfAttorneyNumber)
      copy("receiver.powerOfAttorneyDate", r.powerOfAttorneyDate)
      copy("receiver.idDocumentType", r.idDocumentType)
      copy("receiver.idDocumentSeries", r.idDocumentSeries)
      copy("receiver.idDocumentNumber", r.idDocumentNumber)
      copy("receiver.idDocumentIssuer", r.idDocumentIssuer)
      copy("receiver.idDocumentIssueDate", r.idDocumentIssueDate)
    }
  }

  if (sectionIds.includes("testResults") && detail.testResults) {
    const t = detail.testResults
    copy("testResults.totalSpecificBrakingForce", t.totalSpecificBrakingForce)
    copy("testResults.brakeForceImbalanceRatio", t.brakeForceImbalanceRatio)
    copy("testResults.brakeResponseTime", t.brakeResponseTime)
    copy("testResults.parkingBrakeSpecificForce", t.parkingBrakeSpecificForce)
    copy("testResults.brakeForceOvality", t.brakeForceOvality)
    copy("testResults.engineType", t.engineType)
    copy("testResults.coAtIdleRpm", t.coAtIdleRpm)
    copy("testResults.cnhmAtIdleRpm", t.cnhmAtIdleRpm)
    copy("testResults.coAtElevatedRpm", t.coAtElevatedRpm)
    copy("testResults.cnhmAtElevatedRpm", t.cnhmAtElevatedRpm)
    copy("testResults.lambda", t.lambda)
    copy("testResults.smokeOpacityK", t.smokeOpacityK)
    copy("testResults.windshieldMarked", t.windshieldMarked)
    copy("testResults.windshield", t.windshield)
    copy("testResults.leftSideMarked", t.leftSideMarked)
    copy("testResults.leftSide", t.leftSide)
    copy("testResults.rightSideMarked", t.rightSideMarked)
    copy("testResults.rightSide", t.rightSide)
    copy("testResults.rearWindowMarked", t.rearWindowMarked)
    copy("testResults.rearWindow", t.rearWindow)
    copy("testResults.videoUrl", t.videoUrl)
  }

  return ops
}

export interface DuplicateNonconformitiesPayload {
  hasNonconformities: boolean
  items: (
    | { form: "STANDARD"; nonconformityCodeId: string }
    | { form: "NONSTANDARD"; defectType: OtkDefectSeverity; description: string }
  )[]
}

// `saveOtkDocumentNonconformities` replaces a draft's whole list in one
// call, so duplicating this section means resending every item from the
// source document as-is. An item missing the field its own `form` requires
// (shouldn't happen for an already-saved source, but the type only promises
// it optionally) is dropped rather than sent malformed.
export function buildDuplicateNonconformitiesPayload(
  detail: OtkDocumentDetail
): DuplicateNonconformitiesPayload {
  const items = (detail.nonconformities ?? []).flatMap(
    (n): DuplicateNonconformitiesPayload["items"] => {
      if (n.form === "STANDARD" && n.nonconformityCodeId) {
        return [{ form: "STANDARD", nonconformityCodeId: n.nonconformityCodeId }]
      }
      if (n.form === "NONSTANDARD" && n.defectType && n.description) {
        return [
          {
            form: "NONSTANDARD",
            defectType: n.defectType,
            description: n.description,
          },
        ]
      }
      return []
    }
  )
  return {
    hasNonconformities: detail.hasNonconformities ?? items.length > 0,
    items,
  }
}

// --- Signing (`pidpysannya-dokumenta-otk.md`'s "Вибір спецпродукції без
// резервування" — step 6: "Серія й номер обраної електронної одиниці та, за
// наявності, фізичного бланка входять до повного payload. Браузер серіалізує
// і підписує цей payload.") ---

function requireNumber(raw: string): number {
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : 0
}

function nullableNumber(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === "") return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

function nullableText(raw: string): string | null {
  const trimmed = raw.trim()
  return trimmed === "" ? null : trimmed
}

// Builds the exact byte-for-byte JSON body the KEP signs and
// `POST /otk/documents/:id/sign` verifies against
// (`signedOtkDocumentPayloadSchema`) — from the wizard's own live tab state
// (`DocumentEdit.tsx`) or, for view mode, the `*StateFromDetail` mappers
// fed a stored `OtkDocumentDetail` (`documentCardSections`'s own pattern).
// `files` is deliberately never read off `PhotosTabState` (it only ever
// carries `pathname`, never the `contentType`/`etag` the signed schema
// needs) — callers fetch the server's own record via
// `getOtkDocumentFilesWithUrl` right before signing instead.
export function buildSignedOtkDocumentPayload({
  type,
  result,
  punktId,
  vehicle,
  owner,
  tests,
  nonConformities,
  reequipment,
  files,
  electronicDocumentSeries,
  electronicDocumentNumber,
  blankSeries,
  blankNumber,
}: {
  type: DocumentType
  result: OtkDocumentResult
  punktId: string
  vehicle: VehicleTabState
  owner: OwnerTabState
  tests: TestsTabState
  nonConformities: NonConformitiesTabState
  reequipment: ReequipmentTabState
  files: OtkDocumentPhoto[]
  electronicDocumentSeries: string
  electronicDocumentNumber: string
  blankSeries?: string
  blankNumber?: string
}): SignedOtkDocumentPayload {
  const nonconformities: SignedOtkDocumentPayload["nonconformities"] = nonConformities.found
    ? [
        ...nonConformities.selectedCodes.map((code) => ({
          form: "STANDARD" as const,
          nonconformityCodeId: code.code,
          defectType: code.severity,
          description: code.description || null,
        })),
        ...nonConformities.unlistedIssues.map((issue) => ({
          form: "NONSTANDARD" as const,
          nonconformityCodeId: null,
          defectType: issue.type,
          description: issue.description,
        })),
      ]
    : []

  const reconstructions: SignedOtkDocumentPayload["reconstructions"] = reequipment.hasReequipment
    ? reequipment.conversions.map((conversion) => ({
        approved: conversion.approved,
        description: conversion.description.trim(),
        documentName: nullableText(conversion.docName),
        documentNumber: nullableText(conversion.docNumber),
        documentDate: nullableText(conversion.docDate),
      }))
    : []

  return {
    type,
    result,
    punktId,
    blankSeries: blankSeries ?? null,
    blankNumber: blankNumber ?? null,
    electronicDocumentSeries,
    electronicDocumentNumber,
    files: files.map((file) => {
      if (!file.sha256) throw new Error("Для фото відсутній SHA-256. Оновіть фотоматеріали.")
      return {
        property: file.property,
        sha256: file.sha256,
      }
    }),
    vehicle: {
      plateNumber: vehicle.plate.trim(),
      registrationCertificate: nullableText(vehicle.registrationCertificate),
      vin: nullableText(vehicle.vin),
      bodyNumber: nullableText(vehicle.bodyNumber),
      chassisNumber: nullableText(vehicle.chassisNumber),
      brand: vehicle.brand.trim(),
      model: vehicle.model.trim(),
      category: vehicle.category,
      type: nullableText(vehicle.vehicleType),
      manufactureYear: requireNumber(vehicle.manufactureYear),
      registrationDate: nullableText(vehicle.registrationDate),
      emissionLevel: nullableText(vehicle.ecologyLevel),
      currentOdometer: requireNumber(vehicle.currentOdometer),
      previousOdometer: nullableNumber(vehicle.previousOdometer),
      purposes: vehicle.purposes.length > 0 ? vehicle.purposes : null,
    },
    owner: {
      type: owner.ownerType,
      fullName: nullableText(owner.fullName),
      taxId: nullableText(owner.taxId),
      organizationName: nullableText(owner.orgName),
      edrpou: nullableText(owner.edrpou),
      postalAddress: nullableText(owner.address),
      phone: nullableText(owner.phone),
      email: nullableText(owner.email),
      dateOfBirth: nullableText(owner.birthDate),
      idDocumentType: nullableText(owner.idDocType),
      idDocumentSeries: nullableText(owner.idDocSeries),
      idDocumentNumber: nullableText(owner.idDocNumber),
      idDocumentIssuer: nullableText(owner.idDocIssuedBy),
      idDocumentIssueDate: nullableText(owner.idDocIssuedAt),
    },
    receiverIsOwner: owner.recipientMode === "owner",
    receiver:
      owner.recipientMode === "owner"
        ? null
        : {
            fullName: owner.recFullName.trim(),
            dateOfBirth: nullableText(owner.recBirthDate),
            powerOfAttorneySeries: nullableText(owner.receiptDocSeries),
            powerOfAttorneyNumber: nullableText(owner.receiptDocNumber),
            powerOfAttorneyDate: nullableText(owner.receiptDocDate),
            idDocumentType: nullableText(owner.recIdDocType),
            idDocumentSeries: nullableText(owner.recIdDocSeries),
            idDocumentNumber: nullableText(owner.recIdDocNumber),
            idDocumentIssuer: nullableText(owner.recIdDocIssuedBy),
            idDocumentIssueDate: nullableText(owner.recIdDocIssuedAt),
          },
    isTaxi: Boolean(vehicle.isTaxi),
    ...deriveDangerousGoodsSubmitField(vehicle.purposes),
    testResults: {
      totalSpecificBrakingForce: requireNumber(tests.totalBrakingForce),
      brakeForceImbalanceRatio: nullableNumber(tests.imbalanceRatio),
      brakeResponseTime: nullableNumber(tests.brakeResponseTime),
      parkingBrakeSpecificForce: nullableNumber(tests.parkingBrakeForce),
      brakeForceOvality: nullableNumber(tests.ovality),
      engineType: emissionModeToEngineType[tests.emissionMode],
      coAtIdleRpm: nullableNumber(tests.coIdle),
      cnhmAtIdleRpm: nullableNumber(tests.cnhmIdle),
      coAtElevatedRpm: nullableNumber(tests.coElevated),
      cnhmAtElevatedRpm: nullableNumber(tests.cnhmElevated),
      lambda: nullableNumber(tests.lambda),
      smokeOpacityK: nullableNumber(tests.smokeK),
      windshieldMarked: tests.windshieldExempt,
      windshield: nullableNumber(tests.windshield),
      leftSideMarked: tests.leftExempt,
      leftSide: nullableNumber(tests.left),
      rightSideMarked: tests.rightExempt,
      rightSide: nullableNumber(tests.right),
      rearWindowMarked: tests.rearExempt,
      rearWindow: nullableNumber(tests.rear),
      videoUrl: nullableText(tests.videoUrl),
    },
    nonconformities,
    reconstructions,
    notes: nullableText(vehicle.notes),
  }
}

/**
 * Ukrainian labels for `signedOtkDocumentPayloadSchema` paths, keyed the same
 * way shared's `editableDraftFields` is — dot-joined, no array indices.
 * Wording is copied from this file's own tab `FieldLabel`s
 * (`VehicleTab`/`OwnerTab`/`TestsTab`) and `SECTION_LABELS`/`FormCard`
 * titles (`DocumentEdit.tsx`, `PhotosTab.tsx`) so a validation failure names
 * a field the same way the form already does. Not exhaustive on every leaf —
 * `otkDocumentFieldLabel` falls back to the nearest mapped ancestor (e.g. an
 * unmapped `testResults.*` leaf still resolves to "Результати випробувань")
 * so an unmapped field never surfaces as a raw path.
 */
const signedOtkDocumentFieldLabels: Record<string, string> = {
  type: "Тип документа",
  result: "Результат ОТК",
  punktId: "Пункт ОТК",
  blankSeries: "Серія бланка суворої звітності",
  blankNumber: "Номер бланка суворої звітності",
  electronicDocumentSeries: "Серія електронного документа ОТК",
  electronicDocumentNumber: "Номер електронного документа ОТК",
  receiverIsOwner: "Отримувач є власником ТЗ",
  isTaxi: "Автомобіль експлуатується як таксі",
  transportsDangerousGoods: "Перевезення небезпечних вантажів",
  notes: "Примітки",
  files: "Фото",
  nonconformities: "Невідповідності",
  reconstructions: "Переобладнання",

  vehicle: "Транспортний засіб",
  "vehicle.plateNumber": "Номерний знак",
  "vehicle.registrationCertificate": "Свідоцтво про реєстрацію ТЗ",
  "vehicle.vin": "VIN-код",
  "vehicle.bodyNumber": "Номер кузова",
  "vehicle.chassisNumber": "Номер шасі / рами",
  "vehicle.brand": "Марка",
  "vehicle.model": "Модель",
  "vehicle.category": "Категорія",
  "vehicle.type": "Тип ТЗ",
  "vehicle.manufactureYear": "Рік випуску ТЗ",
  "vehicle.registrationDate": "Дата реєстрації",
  "vehicle.emissionLevel": "Екологічний рівень",
  "vehicle.currentOdometer": "Показник одометра поточного ОТК",
  "vehicle.previousOdometer": "Показник одометра попереднього ОТК",
  "vehicle.purposes": "Призначення ТЗ",

  owner: "Власник",
  "owner.type": "Тип власника",
  "owner.fullName": "ПІБ власника",
  "owner.taxId": "ІПН / РНОКПП власника",
  "owner.organizationName": "Назва організації власника",
  "owner.edrpou": "ЄДРПОУ власника",
  "owner.postalAddress": "Поштова адреса власника",
  "owner.phone": "Телефон власника",
  "owner.email": "Ел. пошта власника",
  "owner.dateOfBirth": "Дата народження власника",
  "owner.idDocumentType": "Тип документа, що посвідчує особу власника",
  "owner.idDocumentSeries": "Серія документа власника",
  "owner.idDocumentNumber": "Номер документа власника",
  "owner.idDocumentIssuer": "Орган, що видав документ власника",
  "owner.idDocumentIssueDate": "Дата видачі документа власника",

  receiver: "Отримувач",
  "receiver.fullName": "ПІБ отримувача",
  "receiver.dateOfBirth": "Дата народження отримувача",
  "receiver.powerOfAttorneySeries": "Серія довіреності",
  "receiver.powerOfAttorneyNumber": "Номер довіреності",
  "receiver.powerOfAttorneyDate": "Дата документа отримання",
  "receiver.idDocumentType": "Тип документа, що посвідчує особу отримувача",
  "receiver.idDocumentSeries": "Серія документа отримувача",
  "receiver.idDocumentNumber": "Номер документа отримувача",
  "receiver.idDocumentIssuer": "Орган, що видав документ отримувача",
  "receiver.idDocumentIssueDate": "Дата видачі документа отримувача",

  testResults: "Результати випробувань",
  "testResults.totalSpecificBrakingForce": "Загальна питома гальмова сила",
  "testResults.brakeForceImbalanceRatio": "Коефіцієнт нерівномірності гальмових сил",
  "testResults.brakeResponseTime": "Тривалість спрацювання робочої гальмової системи",
  "testResults.parkingBrakeSpecificForce": "Питома гальмова сила стоянкової гальмової системи",
  "testResults.brakeForceOvality": "Овальність гальмівних сил",
  "testResults.engineType": "Тип випробування спалин",
  "testResults.coAtIdleRpm": "CO при мінімальній частоті",
  "testResults.cnhmAtIdleRpm": "CnHm при мінімальній частоті",
  "testResults.coAtElevatedRpm": "CO при підвищеній частоті",
  "testResults.cnhmAtElevatedRpm": "CnHm при підвищеній частоті",
  "testResults.lambda": "Коефіцієнт надміру повітря, λ",
  "testResults.smokeOpacityK": "Димність спалин K",
  "testResults.windshield": "Світлопропускання вітрового скла",
  "testResults.leftSide": "Світлопропускання бокового лівого скла",
  "testResults.rightSide": "Світлопропускання бокового правого скла",
  "testResults.rearWindow": "Світлопропускання заднього скла",
  "testResults.videoUrl": "Посилання на відеофайл щодо відеофіксації процесу ОТК",
}

/**
 * Resolves a zod issue `path` to a Ukrainian label, trying the exact
 * dot-joined path first, then the same path with array indices stripped
 * (`nonconformities.0.description` → `nonconformities.description`), then
 * just its root segment — so an unmapped leaf still names the section it
 * belongs to instead of falling through to a raw path string.
 */
export function otkDocumentFieldLabel(path: ReadonlyArray<PropertyKey>): string {
  const segments = path.map(String)
  const exact = segments.join(".")
  if (exact in signedOtkDocumentFieldLabels) return signedOtkDocumentFieldLabels[exact]!
  const withoutIndices = segments.filter((segment) => !/^\d+$/.test(segment)).join(".")
  if (withoutIndices in signedOtkDocumentFieldLabels) {
    return signedOtkDocumentFieldLabels[withoutIndices]!
  }
  const root = segments.find((segment) => !/^\d+$/.test(segment))
  if (root && root in signedOtkDocumentFieldLabels) return signedOtkDocumentFieldLabels[root]!
  return exact || "Документ"
}

// Ukrainian for the sign dialog's refusal notice — mirrors
// `destructionActApiErrorMessages` (`lib/destructionAct.ts`)'s own pattern,
// one entry per error code `POST /otk/documents/:id/sign`
// (`otk-documents.ts`) can actually return.
const documentSignApiErrorMessages: Record<string, string> = {
  INVALID_KEP_SIGNATURE:
    "Не вдалося перевірити КЕП-підпис. Перевірте носій ключа і повторіть спробу.",
  SIGNER_MISMATCH:
    "Підпис належить іншому користувачу. Використайте свій особистий ключ.",
  DOCUMENT_NOT_FOUND: "Документ не знайдено. Оновіть сторінку і спробуйте ще раз.",
  DOCUMENT_NOT_DRAFT: "Документ уже не є чернеткою — підписання неможливе.",
  DOCUMENT_VALIDATION_FAILED:
    "Дані документа не пройшли перевірку. Перевірте заповнені поля і спробуйте ще раз.",
  SIGNED_PAYLOAD_MISMATCH:
    "Підписані дані не відповідають поточному документу. Оновіть сторінку й повторіть підписання.",
  BLANK_NOT_ELIGIBLE:
    "Обрану спецпродукцію вже використано. Оновіть список і оберіть інші одиниці.",
}

/**
 * The document a failed sign/retry call left behind. `submitOtkDocument`
 * commits the signed document (`SUBMISSION_PENDING`, units consumed) before
 * the first registry attempt, so `502 SUBMISSION_FAILED` and
 * `425 SUBMISSION_NOT_READY` (`otk-document-submission.ts`) both carry the
 * persisted `document` — it is signed and must be retried, not re-signed
 * (re-signing would hit `409 DOCUMENT_NOT_DRAFT`).
 */
export function pendingDocumentFromError(err: unknown): OtkDocumentDetail | null {
  if (!isAxiosError(err)) return null
  const parsed = otkDocumentDetailSchema.safeParse(
    (err.response?.data as { document?: unknown } | undefined)?.document
  )
  return parsed.success ? parsed.data : null
}

/** Extracts a user-facing message from a failed `retryOtkDocument` call. */
export function documentRetryErrorMessage(err: unknown): string {
  if (isAxiosError(err) && err.response?.status === 425) {
    return "Повторне надсилання ще недоступне."
  }
  return "Не вдалося повторно надіслати документ. Спробуйте пізніше."
}

/** Extracts a user-facing message from a failed `submitSignedOtkDocument` call. */
export function documentSignErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const code = (err.response?.data as { error?: string } | undefined)?.error
    if (code && code in documentSignApiErrorMessages) {
      return documentSignApiErrorMessages[code]!
    }
  }
  return "Не вдалося підписати та відправити документ. Спробуйте ще раз."
}
